import { useMemo } from 'react';
import DatePicker from './DatePicker.jsx';

/**
 * Sprint 8 — Picker UI pour configurer une récurrence (Allocation / TimeOff).
 * Pattern Float / Google Calendar : presets + custom weekly + end condition.
 *
 * Le composant manipule un état applicatif simple (props `value` + `onChange`),
 * puis génère la string RRULE via `buildRRuleClient(value)` à la sauvegarde
 * (cf. helper exporté en bas du fichier — équivalent client de
 * `api/src/lib/recurrence.ts > buildRRule`, sans dépendre de la lib rrule
 * côté front pour rester léger).
 *
 * Props :
 *  - value: { freq, byweekday, interval, endType, count, until }
 *  - onChange(v): callback de mise à jour
 *  - disabled?: boolean
 */

const PRESETS = [
  { key: 'none', label: 'Ne se répète pas' },
  { key: 'daily', label: 'Tous les jours' },
  { key: 'weekdays', label: 'Du lundi au vendredi' },
  { key: 'weekly', label: 'Toutes les semaines' },
  { key: 'biweekly', label: 'Toutes les 2 semaines' },
  { key: 'monthly', label: 'Tous les mois' },
];

const DAYS = [
  { code: 'MO', label: 'L' },
  { code: 'TU', label: 'M' },
  { code: 'WE', label: 'M' },
  { code: 'TH', label: 'J' },
  { code: 'FR', label: 'V' },
  { code: 'SA', label: 'S' },
  { code: 'SU', label: 'D' },
];

export default function RecurrencePicker({ value, onChange, disabled = false }) {
  const v = value ?? { freq: 'none' };

  const setFreq = (freq) => onChange({ ...v, freq });
  const setEndType = (endType) =>
    onChange({
      ...v,
      endType,
      ...(endType === 'never' ? { count: undefined, until: undefined } : {}),
      ...(endType === 'count' && !v.count ? { count: 10 } : {}),
      ...(endType === 'until' && !v.until ? { until: '' } : {}),
    });

  const showWeekdayPicker = v.freq === 'weekly';
  const showEndCondition = v.freq !== 'none';
  // Unité de la condition de fin "Après N …" (ticket #7) : pour les récurrences
  // hebdo, l'utilisateur raisonne en SEMAINES, pas en jours individuels.
  const endUnit = { monthly: 'mois', daily: 'jours' }[v.freq] ?? 'semaines';

  const toggleDay = (code) => {
    const cur = v.byweekday ?? [];
    const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
    onChange({ ...v, byweekday: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontWeight: 500 }}>Récurrence</span>
        <select
          value={v.freq}
          onChange={(e) => setFreq(e.target.value)}
          disabled={disabled}
          style={{ padding: '8px 10px', borderRadius: 'var(--radius-md, 8px)' }}
        >
          {PRESETS.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
      </label>

      {showWeekdayPicker && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Jours de la semaine</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {DAYS.map((d) => {
              const selected = (v.byweekday ?? []).includes(d.code);
              return (
                <button
                  key={d.code}
                  type="button"
                  onClick={() => toggleDay(d.code)}
                  disabled={disabled}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 999,
                    border: `1px solid ${selected ? 'var(--accent)' : 'var(--glass-border)'}`,
                    background: selected ? 'var(--accent)' : 'transparent',
                    color: selected ? 'white' : 'var(--text)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                  }}
                  aria-pressed={selected}
                  title={d.code}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showEndCondition && (
        <fieldset
          style={{
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md, 8px)',
            padding: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <legend style={{ padding: '0 6px', fontSize: 13, color: 'var(--text-muted)' }}>Fin</legend>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="radio"
              checked={(v.endType ?? 'never') === 'never'}
              onChange={() => setEndType('never')}
              disabled={disabled}
            />
            Jamais
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="radio"
              checked={v.endType === 'count'}
              onChange={() => setEndType('count')}
              disabled={disabled}
            />
            Après{' '}
            <input
              type="number"
              min={1}
              max={365}
              value={v.count ?? 10}
              onChange={(e) => onChange({ ...v, endType: 'count', count: Number(e.target.value) })}
              disabled={disabled || v.endType !== 'count'}
              style={{ width: 60, padding: '4px 6px' }}
            />{' '}
            {endUnit}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="radio"
              checked={v.endType === 'until'}
              onChange={() => setEndType('until')}
              disabled={disabled}
            />
            Le{' '}
            <DatePicker
              value={v.until ?? ''}
              onChange={(val) => onChange({ ...v, endType: 'until', until: val })}
              disabled={disabled || v.endType !== 'until'}
              style={{ width: 'auto', padding: '4px 6px' }}
            />
          </label>
        </fieldset>
      )}
    </div>
  );
}

// ---------- Helpers buildRRule / parseRRule côté front ----------
//
// On reste sans dépendance externe : on génère la string RRULE manuellement
// depuis la valeur applicative. Symétrique de api/src/lib/recurrence.ts > buildRRule.
//
// `value` est l'objet { freq, byweekday, interval, endType, count, until }.

/**
 * Nombre d'occurrences RFC (= jours générés) par "période" choisie par
 * l'utilisateur (ticket #7). Pour une récurrence hebdo, une période = 1 semaine
 * qui contient N jours actifs (5 pour lun→ven, sinon le nombre de jours
 * sélectionnés). `COUNT` en RFC 5545 compte les occurrences (jours), pas les
 * semaines : il faut donc multiplier le compte "semaines" saisi par ce facteur.
 */
function daysPerPeriod(freq, byweekday) {
  if (freq === 'weekdays') return 5;
  if (freq === 'weekly' || freq === 'biweekly') {
    return Array.isArray(byweekday) && byweekday.length > 0 ? byweekday.length : 1;
  }
  return 1; // daily, monthly : 1 occurrence par période
}

export function buildRRuleClient(value) {
  if (!value || !value.freq || value.freq === 'none') return null;

  const parts = [];
  switch (value.freq) {
    case 'daily':
      parts.push('FREQ=DAILY');
      if (value.interval && value.interval > 1) parts.push(`INTERVAL=${value.interval}`);
      break;
    case 'weekdays':
      parts.push('FREQ=WEEKLY');
      parts.push('BYDAY=MO,TU,WE,TH,FR');
      break;
    case 'weekly':
      parts.push('FREQ=WEEKLY');
      if (Array.isArray(value.byweekday) && value.byweekday.length > 0) {
        parts.push(`BYDAY=${value.byweekday.join(',')}`);
      }
      if (value.interval && value.interval > 1) parts.push(`INTERVAL=${value.interval}`);
      break;
    case 'biweekly':
      parts.push('FREQ=WEEKLY');
      parts.push('INTERVAL=2');
      if (Array.isArray(value.byweekday) && value.byweekday.length > 0) {
        parts.push(`BYDAY=${value.byweekday.join(',')}`);
      }
      break;
    case 'monthly':
      parts.push('FREQ=MONTHLY');
      if (value.interval && value.interval > 1) parts.push(`INTERVAL=${value.interval}`);
      break;
    default:
      return null;
  }

  if (value.endType === 'count' && value.count) {
    const rfcCount = value.count * daysPerPeriod(value.freq, value.byweekday);
    parts.push(`COUNT=${rfcCount}`);
  } else if (value.endType === 'until' && value.until) {
    // value.until = string "YYYY-MM-DD"
    const compact = value.until.replace(/-/g, '');
    parts.push(`UNTIL=${compact}T000000Z`);
  }

  return parts.join(';');
}

/**
 * Parse une string RRULE en objet applicatif (pour pré-remplir le picker
 * lors de l'édition d'une série existante). Best-effort, ne couvre que les
 * patterns que `buildRRuleClient` peut générer.
 */
export function parseRRuleClient(rrule) {
  const empty = { freq: 'none', byweekday: [], endType: 'never' };
  if (!rrule || typeof rrule !== 'string') return empty;
  const cleaned = rrule.startsWith('RRULE:') ? rrule.slice(6) : rrule;
  const parts = Object.fromEntries(
    cleaned.split(';').map((p) => {
      const [k, vv] = p.split('=');
      return [k?.toUpperCase(), vv];
    }),
  );

  const freqRaw = (parts.FREQ ?? '').toUpperCase();
  const interval = parts.INTERVAL ? Number(parts.INTERVAL) : 1;
  const byday = parts.BYDAY ? parts.BYDAY.split(',') : [];

  let freq = 'none';
  if (freqRaw === 'DAILY') freq = 'daily';
  else if (freqRaw === 'WEEKLY') {
    if (interval === 2) freq = 'biweekly';
    else if (
      byday.length === 5 &&
      ['MO', 'TU', 'WE', 'TH', 'FR'].every((d) => byday.includes(d))
    )
      freq = 'weekdays';
    else freq = 'weekly';
  } else if (freqRaw === 'MONTHLY') freq = 'monthly';

  const out = {
    freq,
    byweekday: byday,
    interval: interval > 1 && freq !== 'biweekly' && freq !== 'weekdays' ? interval : undefined,
    endType: 'never',
  };
  if (parts.COUNT) {
    out.endType = 'count';
    // Reconvertit le COUNT RFC (jours) en nombre de périodes (semaines/mois)
    // affiché à l'utilisateur — inverse de buildRRuleClient (ticket #7).
    const perPeriod = daysPerPeriod(freq, byday);
    out.count = Math.max(1, Math.round(Number(parts.COUNT) / perPeriod));
  } else if (parts.UNTIL) {
    out.endType = 'until';
    // "20260930T000000Z" → "2026-09-30"
    out.until = `${parts.UNTIL.slice(0, 4)}-${parts.UNTIL.slice(4, 6)}-${parts.UNTIL.slice(6, 8)}`;
  }
  return out;
}

/**
 * Rendu humain bref d'une RRULE pour l'afficher dans un libellé.
 */
export function describeRRuleClient(rrule) {
  if (!rrule) return 'Pas de récurrence';
  const v = parseRRuleClient(rrule);
  const base = {
    daily: 'Tous les jours',
    weekdays: 'Du lundi au vendredi',
    weekly: 'Toutes les semaines',
    biweekly: 'Toutes les 2 semaines',
    monthly: 'Tous les mois',
  }[v.freq] ?? 'Récurrence personnalisée';
  let suffix = '';
  if (v.endType === 'count') suffix = ` × ${v.count}`;
  else if (v.endType === 'until') suffix = ` jusqu'au ${v.until}`;
  return `${base}${suffix}`;
}
